import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  const [loading, setLoading] = useState(true);
  const refreshPromiseRef = useRef(null);
  const didMountFetch = useRef(false);

  // Fetch user on initial mount only
  useEffect(() => {
    // On the invite-accept page, clear any stale session so no fetchUser / interceptor
    // race can corrupt the fresh tokens that acceptInvite will store.
    if (window.location.pathname.includes('/invite/accept')) {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setRefreshToken(null);
      setLoading(false);
      return;
    }
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up axios interceptor for automatic token refresh on 401

  useEffect(() => {

    const requestInterceptor = axios.interceptors.request.use(
        (config) => {

            if (
                config.url &&
                config.url.includes("/vision/")
            ) {

                const agentToken = sessionStorage.getItem(
                    "agent_access_vision"
                );

                

                if (agentToken) {

                    config.headers = config.headers || {};

                    config.headers["X-Agent-Access"] =
                        agentToken;

                }

            }

            return config;
        }
    );

    return () => {
        axios.interceptors.request.eject(
            requestInterceptor
        );
    };

}, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const skipRefresh = !!(
          originalRequest?.headers?.['X-Skip-Auth-Refresh'] ||
          originalRequest?.headers?.['x-skip-auth-refresh']
        );

        // Read latest refresh token from localStorage (avoids stale closure)
        const currentRefresh = localStorage.getItem('refresh_token');

        if (
          originalRequest &&
          error.response?.status === 401 &&
          !originalRequest._retry &&
          currentRefresh &&
          !skipRefresh &&
          !originalRequest.url?.includes('/auth/refresh') &&
          !originalRequest.url?.includes('/auth/login')
        ) {
          originalRequest._retry = true;
          try {
            if (!refreshPromiseRef.current) {
              refreshPromiseRef.current = axios
                .post(`${API}/auth/refresh`, {
                  refresh_token: currentRefresh,
                })
                .then((res) => {
                  const { access_token: newAccess, refresh_token: newRefresh } = res.data;
                  // Guard: only store refreshed tokens if no other auth flow (e.g. invite accept) replaced the session
                  if (localStorage.getItem('refresh_token') === currentRefresh) {
                    localStorage.setItem('token', newAccess);
                    localStorage.setItem('refresh_token', newRefresh);
                    setToken(newAccess);
                    setRefreshToken(newRefresh);
                  }
                  return newAccess;
                })
                .finally(() => {
                  refreshPromiseRef.current = null;
                });
            }

            const newAccess = await refreshPromiseRef.current;
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            return await axios(originalRequest);
          } catch (refreshErr) {
            // Only clear session if no other auth flow has replaced the refresh token since we started
            if (!localStorage.getItem('refresh_token') || localStorage.getItem('refresh_token') === currentRefresh) {
              clearLocalSession();
            }
            return Promise.reject(refreshErr);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUser = async () => {
    // Read token outside try so it's accessible in catch
    const currentToken = localStorage.getItem('token');
    try {
      if (!currentToken) {
        setUser(null);
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      // Guard: only update user if our token is still the active one (another auth flow may have replaced it)
      if (localStorage.getItem('token') === currentToken) {
        setUser(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Don't call logout() here — the interceptor handles 401 refresh.
      // If refresh also failed, interceptor already cleared the session.
      // Only clear user if no other auth flow has established a new session
      if (!localStorage.getItem('token') || localStorage.getItem('token') === currentToken) {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, refresh_token: rt, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('refresh_token', rt || '');
    setToken(access_token);
    setRefreshToken(rt || '');
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, refresh_token: rt, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('refresh_token', rt || '');
    setToken(access_token);
    setRefreshToken(rt || '');
    setUser(userData);
    return userData;
  };

    // Clear stale auth state so interceptors/fetchUser don't interfere with a fresh invite session
    const clearSessionForInvite = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      refreshPromiseRef.current = null;
      setToken(null);
      setRefreshToken(null);
      setUser(null);
    };

    const acceptInvite = async (inviteToken, email, password, name) => {
    // 1. Nuke any stale session to prevent interceptor race conditions
    clearSessionForInvite();

    // 2. Call backend (no Authorization header will be sent since we cleared it)
    const response = await axios.post(`${API}/invite/accept`, {
      token: inviteToken, email, password, name,
    });
    const { token: accessToken, refresh_token: rt, user: userData } = response.data;

    if (!accessToken || typeof accessToken !== 'string' || accessToken.split('.').length !== 3) {
      throw new Error('Server returned an invalid access token');
    }

    // 3. Store new session atomically
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refresh_token', rt || '');
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    setRefreshToken(rt || '');
    setUser(userData);
    return userData;
};

  const clearLocalSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const logout = async ({ logoutAll = false } = {}) => {
    const access = localStorage.getItem('token');
    const refresh = localStorage.getItem('refresh_token');

    clearLocalSession();

    if (access) {
      try {
        await axios.post(
          `${API}/auth/logout`,
          { refresh_token: refresh || '', logout_all: logoutAll },
          {
            headers: {
              Authorization: `Bearer ${access}`,
              'X-Skip-Auth-Refresh': 'true',
            },
            timeout: 3000,
          }
        );
      } catch (err) {
        // Ignore network/revocation failures after local sign-out.
      }
    }
  };

  const updateCredits = (newCredits) => {
    if (user) {
      setUser({ ...user, credits: newCredits });
    }
  };

  // RBAC helpers
  const roles = user?.roles || [];
  const hasRole = (role) => roles.includes(role);
  const hasAnyRole = (...requiredRoles) => requiredRoles.some((r) => roles.includes(r));
  const isAdmin = hasAnyRole('admin', 'superadmin');
  const isSuperAdmin = hasRole('superadmin');

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      updateCredits,
      isAuthenticated: !!user,
      roles,
      hasRole,
      hasAnyRole,
      isAdmin,
      isSuperAdmin,
      acceptInvite,
      clearSessionForInvite,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
